import React, { useState } from 'react';
import { Path, useForm } from 'react-hook-form';
import KaAlert from '../Common/KaAlert';
import KaModal from '../Common/KaModal';
import SidePaneLayout from '../Common/SidePaneLayout';
import SpinnerButton from '../Common/SpinnerButton';
import { FormCheckbox } from '../Common/FormCheckbox';
import { RowFormText } from '../Common/Forms/FormText';
import { RowFormSelectSearch } from '../Common/Forms/SelectSearch';
import useDisplayMessage from '../Common/useDisplayMessage';
import { useUser } from '../UserAccess/useUser';
import { useCreateBusinessUser, useUpdateBusinessUser } from '../Requests/useUpsertBusinessUser';
import { BusinessSiteDto, BusinessUserDto, UserRoleDto } from '../Requests/useGetUsers';
import { toRoleSelectOptions, UserRoleNames } from './userRoles';
import { UserSiteAccess, UserSitePane } from './UserSitePane';

interface CreateEditUserProps {
  businessUser: BusinessUserDto | null;
  roles: UserRoleDto[];
  sites: BusinessSiteDto[];
  onHide: () => void;
  onSuccess: (emailAddress: string) => void;
}

interface UserForm {
  emailAddress: string;
  userRoleId: string;
  errorRecipient: boolean;
}

const defaultRole = (roles: UserRoleDto[]) =>
  roles.find(r => r.name === UserRoleNames.standard) ?? roles[0];

const initialAccess = (businessUser: BusinessUserDto | null): UserSiteAccess =>
  businessUser
    ? { allSites: !businessUser.siteRestricted, siteIds: [...businessUser.siteIds] }
    : { allSites: true, siteIds: [] };

export const CreateEditUser = (props: CreateEditUserProps) => {
  const displayMessage = useDisplayMessage();
  const { isDevelopment } = useUser();
  const isEditing = !!props.businessUser;

  const initialRoleId = props.businessUser?.userRoleId ?? defaultRole(props.roles)?.id;
  const [roleId, setRoleId] = useState(initialRoleId);
  const [access, setAccess] = useState<UserSiteAccess>(initialAccess(props.businessUser));
  const [showSiteError, setShowSiteError] = useState(false);

  const isAdministrator = !!props.roles.find(r => r.id === roleId)?.administrator;
  const grantsAllSites = isAdministrator || props.sites.length === 0 || access.allSites;
  const sitesValid = grantsAllSites || access.siteIds.length > 0;

  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm<UserForm>({
    defaultValues: {
      emailAddress: '',
      userRoleId: initialRoleId,
      errorRecipient: props.businessUser?.isErrorRecipient ?? false,
    }
  });

  const create = useCreateBusinessUser({
    onSuccess: () => props.onSuccess(getValues('emailAddress')),
    onError: (e) => displayMessage.fail(e.message),
  });

  const update = useUpdateBusinessUser(props.businessUser?.id ?? '', {
    onSuccess: () => props.onSuccess(props.businessUser?.emailAddress ?? ''),
    onError: (e) => displayMessage.fail(e.message),
  });

  const submit = (form: UserForm) => {
    if (!sitesValid) return;

    const siteAccess = {
      siteRestricted: !grantsAllSites,
      siteIds: grantsAllSites ? [] : access.siteIds,
    };

    if (isEditing)
      update.request({ userRoleId: form.userRoleId, errorRecipient: form.errorRecipient, ...siteAccess });
    else
      create.request({
        emailAddress: form.emailAddress,
        userRoleId: form.userRoleId,
        addAsErrorRecipient: form.errorRecipient,
        ...siteAccess
      });
  };

  const save = () => {
    setShowSiteError(true);
    handleSubmit(submit)();
  };

  const accessChanged = (updated: UserSiteAccess) => {
    setAccess(updated);
    setShowSiteError(false);
  };

  const roleChanged = (value: string) => {
    setRoleId(value);
    setShowSiteError(false);
    setValue('userRoleId', value, { shouldValidate: true });
  };

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  };

  const hookProps = (name: Path<UserForm>) => ({
    labelSpace: 4,
    inputSpace: 8,
    name,
    setValue,
    register,
    errors,
  });

  return (
    <KaModal
      onHide={onClose}
      show={true}
      size="lg"
      title={isEditing ? `Edit ${props.businessUser!.emailAddress}` : 'Add user to business'}
      body={(<>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <SidePaneLayout
          paneTitle="Site Access"
          pane={isDevelopment && <UserSitePane
            sites={props.sites}
            isAdministrator={isAdministrator}
            access={access}
            onChange={accessChanged}
            showError={showSiteError} />}>
          {!isEditing &&
            <RowFormText displayName="User Email"
              {...hookProps('emailAddress')}
              options={{
                required: 'Email Address is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email Address is invalid'
                }
              }} />}
          <RowFormSelectSearch displayName="Role"
            id="userRoleId"
            {...hookProps('userRoleId')}
            selectedValue={initialRoleId}
            hideSearch={true}
            onChange={ev => roleChanged(ev.target.value)}
            selectOptions={toRoleSelectOptions(props.roles)} />
          <FormCheckbox
            id="errorRecipient"
            label="Subscribe to connector error emails"
            defaultChecked={props.businessUser?.isErrorRecipient}
            onChange={e => setValue('errorRecipient', e.target.checked)}
            offset={4} />
        </SidePaneLayout>
      </>)}
      footer={(
        <SpinnerButton
          className="btn-ghost-primary"
          spinning={create.isLoading || update.isLoading}
          onClick={save}>
          {isEditing ? 'Save' : 'Add'}
        </SpinnerButton>)
      }
    />
  );
};
